import type { DateTimeValue } from "@carbon6/c6-frontend-design";
import type { CreateTrainingScheduleReq, TrainingScheduleRecurrence } from "@/api/trainingSchedule";
import { isFullSha } from "@/components/table/branchHash";
import { validateCronExpression } from "@/modules/training/public";
import { parseUtcMs } from "@/utils/datetime";

/**
 * "새 Schedule" 위저드의 스케줄 초안. 제출 전체 payload 에 추가할 반복 축·기간·실행 게이트다.
 *
 * 예약은 제출과 **같은 payload** 에 반복 축만 추가해 보낸다(BE `submit` 필드가 단건 제출과
 * 같은 타입). 그래서 별도 폼을 만들지 않고 위저드의 Schedule 스텝이 이 초안을 채운다.
 * `enabled` 토글은 없다. 이 위저드의 목적은 스케줄 생성이라 "등록하지 않음" 상태가 없다.
 *
 * 초안은 폼(`SubmitFormState`)이 아니라 컨트롤러가 가진다. 제출 payload 에 포함되지 않는
 * 값이라 폼에 두면 payload 빌더가 무시해야 할 필드가 늘어난다.
 *
 * wire 에 보낼 값과 생략할 값은 `scheduleCreateFields` 한 곳이 소유한다
 * (docs/rules.md §16. 기본값과 같은 값을 보내는 노이즈 금지).
 */

// 상류 하한. 이보다 짧은 간격은 BE 가 422 로 거부한다.
export const MIN_SCHEDULE_INTERVAL_MINUTES = 5;
// 상한. 상류 컬럼이 32비트 정수라 그 이상은 500 오류가 난다. 1년보다 긴 반복은 뜻이 없다.
export const MAX_SCHEDULE_INTERVAL_MINUTES = 365 * 24 * 60;

// 상류 `min_free_gpus` 범위(1..1024)의 사본. 넘으면 registry 가 422 로 거부한다.
export const MIN_FREE_GPUS_MIN = 1;
export const MIN_FREE_GPUS_MAX = 1024;

export type ScheduleRecurrenceMode = "interval" | "cron";

// 두 자리 0 채움. datetime-local 조립과 cron 풀이가 함께 쓴다.
const pad2 = (value: number): string => String(value).padStart(2, "0");

export type ScheduleDraft = {
  name: string;
  description: string;
  mode: ScheduleRecurrenceMode;
  // 분 단위로 받는다. 상류는 초를 받지만 사람은 반복 주기를 초로 세지 않는다.
  intervalMinutes: string;
  cron: string;
  timezone: string;
  // `datetime-local` 입력값(로컬 wall-clock). "" = 즉시 시작 / 무기한.
  startAt: string;
  endAt: string;
  // 실행 게이트 3축. skip 은 branch learning-core recipe 가 전제라 사용 가능 판정은
  // `isBranchRef` 로 호출부(Schedule 스텝)가 한다.
  skipIfUnchanged: boolean;
  // "" = 게이트 없음. 범위 밖·비정수는 invalid 로 제출을 막는다(scheduleMinFreeGpus).
  minFreeGpus: string;
  trackLatestEvalRelease: boolean;
};

export const EMPTY_SCHEDULE_DRAFT: ScheduleDraft = {
  name: "",
  description: "",
  mode: "interval",
  intervalMinutes: String(MIN_SCHEDULE_INTERVAL_MINUTES),
  cron: "",
  // 반복 규칙은 local wall-clock 이라 시간대가 있어야 뜻이 정해진다. 브라우저 시간대를
  // 기본으로 두면 "매일 09시"가 보통 사용자가 기대하는 09시가 된다.
  timezone: resolveBrowserTimeZone(),
  startAt: "",
  endAt: "",
  skipIfUnchanged: false,
  minFreeGpus: "",
  trackLatestEvalRelease: false,
};

function resolveBrowserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

// 초 간격을 사람이 읽는 주기로 바꾼다. 상류 하한이 300초라 분 미만은 오지 않는다. 나누어떨어지지
// 않는 값도 그대로 표시한다. 반올림하면 5분과 5분 30초가 같아 보인다.
export function intervalLabel(seconds: number): string {
  if (seconds % 86_400 === 0) {
    return `${seconds / 86_400}일마다`;
  }
  if (seconds % 3_600 === 0) {
    return `${seconds / 3_600}시간마다`;
  }
  if (seconds % 60 === 0) {
    return `${seconds / 60}분마다`;
  }
  return `${seconds}초마다`;
}

// 분 입력 → 초. 십진 정수만 받는다("1e3"·"0x10" 은 Number 로는 정수지만 사람이 적은 분이
// 아니다). 하한 미만·상한 초과면 null 이다(제출 게이트가 막는다).
export function scheduleIntervalSeconds(draft: ScheduleDraft): number | null {
  const raw = draft.intervalMinutes.trim();
  if (!/^\d+$/.test(raw)) {
    return null;
  }
  const minutes = Number(raw);
  if (minutes < MIN_SCHEDULE_INTERVAL_MINUTES || minutes > MAX_SCHEDULE_INTERVAL_MINUTES) {
    return null;
  }
  return minutes * 60;
}

// min_free_gpus 입력을 세 경우로 판독한다. 비움(게이트 없음)과 잘못 채움(제출 차단)은 다른 상태다.
// 비움을 invalid 로 판정하면 게이트를 쓰지 않는 보통 경로가 막힌다. 잘못 채움을 비움으로
// 판정하면 오타가 알림 없이 게이트를 지운다.
export type MinFreeGpusDraftValue =
  | { kind: "unset" }
  | { kind: "value"; value: number }
  | { kind: "invalid" };

// 상한은 상류 범위(1024)지만, 클러스터 GPU 총 수를 알면 그 수가 실질 상한이다. 총 수보다 큰
// 조건은 영원히 보류된다. 총 수를 읽지 못했으면(null) 상류 범위를 쓴다.
export function resolveMinFreeGpusMax(totalGpus: number | null): number {
  return totalGpus === null ? MIN_FREE_GPUS_MAX : Math.min(totalGpus, MIN_FREE_GPUS_MAX);
}

export function scheduleMinFreeGpus(
  draft: ScheduleDraft,
  totalGpus: number | null = null,
): MinFreeGpusDraftValue {
  const raw = draft.minFreeGpus.trim();
  if (raw === "") {
    return { kind: "unset" };
  }
  const value = Number(raw);
  const max = resolveMinFreeGpusMax(totalGpus);
  if (!Number.isInteger(value) || value < MIN_FREE_GPUS_MIN || value > max) {
    return { kind: "invalid" };
  }
  return { kind: "value", value };
}

/**
 * 여유 GPU 조건 안내문. 생성·편집이 같은 문구를 쓴다. 총 수를 알면 그 수를 상한으로 표시한다.
 * 비움의 뜻은 행에 조건이 이미 있는지(`clearsExisting`)에 따라 다르다.
 */
export function minFreeGpusGuide({
  totalGpus,
  clearsExisting,
}: {
  totalGpus: number | null;
  clearsExisting: boolean;
}): string {
  const max = resolveMinFreeGpusMax(totalGpus);
  const range =
    totalGpus === null
      ? `${MIN_FREE_GPUS_MIN}~${max} 사이 정수로 적어 주세요.`
      : `지금 클러스터의 GPU는 총 ${totalGpus}개라 ${MIN_FREE_GPUS_MIN}~${max} 사이 정수로 적어 주세요.`;
  const empty = clearsExisting
    ? "비우면 조건을 지우고 조건 없이 실행합니다."
    : "비우면 조건 없이 실행합니다.";
  return `실행 시각에 여유 GPU가 이 수보다 적으면 여유가 생길 때까지 실행을 보류합니다. ${range} ${empty}`;
}

// 검증 실패 문구. 안내문과 같은 상한을 표시한다.
export function minFreeGpusInvalidMessage(totalGpus: number | null): string {
  return `여유 GPU 조건은 ${MIN_FREE_GPUS_MIN}~${resolveMinFreeGpusMax(totalGpus)} 사이 정수로 입력해 주세요.`;
}

// 40자 hex 는 커밋 고정이라 branch 추종(skip_if_unchanged)이 성립하지 않는다. 39·41자이거나
// hex 가 아닌 문자가 섞이면 branch 이름일 수 있으므로 true 다. 최종 판정은 상류가 한다. FE 가
// 이 predicate 를 더 정교하게 만들면 상류 판정과 구현이 두 벌이 된다. 40자 hex 판정은 코드 출처
// 표시와 같은 `isFullSha` 를 쓴다.
export function isBranchRef(ref: string): boolean {
  const trimmed = ref.trim();
  return trimmed !== "" && !isFullSha(trimmed);
}

// `datetime-local` 값의 형태. 브라우저에 따라 초가 붙기도 한다.
const DATETIME_LOCAL_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;

/**
 * `datetime-local` 값(로컬 wall-clock) → aware UTC instant ISO.
 *
 * registry 가 instant 필드(start_at/end_at)를 aware UTC 로 정규화하므로 `Z` 를 붙여
 * 보낸다. 그래서 `parseUtcMs` 와 왕복이 성립한다. 빈 값·파싱 불가는 null 이다(키 생략).
 *
 * `parseUtcMs` 로 읽지 않는 이유: 그 파서는 naive 를 **UTC** 로 읽는 wire 규칙이다. 이 값은
 * 사용자의 로컬 시각이다. 그래서 로컬 성분으로 Date 를 만들고 instant 로 바꾼다.
 */
export function datetimeLocalToUtcIso(local: string): string | null {
  const match = DATETIME_LOCAL_PATTERN.exec(local.trim());
  if (match === null) {
    return null;
  }
  const [, year, month, day, hour, minute, second] = match;
  const date = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second ?? "0"),
  );
  // Date 생성자는 범위 밖 성분을 받으면 다음 단위로 넘긴다(13월 → 이듬해 1월). 손으로
  // 입력한 오타가 알림 없이 다른 시각이 되지 않도록 성분이 그대로인지 다시 확인한다.
  const intact =
    date.getFullYear() === Number(year) &&
    date.getMonth() === Number(month) - 1 &&
    date.getDate() === Number(day) &&
    date.getHours() === Number(hour) &&
    date.getMinutes() === Number(minute);
  return intact ? date.toISOString() : null;
}

/**
 * aware UTC instant ISO → `datetime-local` 값(로컬 wall-clock). `datetimeLocalToUtcIso` 의 역이다.
 * 편집 폼이 저장된 end_at 을 입력 초기값으로 되돌릴 때 쓴다. 없거나 파싱할 수 없으면 빈 값이다.
 */
export function utcIsoToDatetimeLocal(iso: string | null | undefined): string {
  if (iso == null || iso === "") {
    return "";
  }
  const ms = parseUtcMs(iso);
  if (ms === null) {
    return "";
  }
  const date = new Date(ms);
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(
    date.getHours(),
  )}:${pad2(date.getMinutes())}`;
}

/**
 * `datetime-local` 값 ↔ c6 `DateTimePicker` 값. 초안은 항상 문자열을 정본으로 가진다(판독기·PATCH
 * 빌더·요약이 문자열을 읽는다). 변환은 피커 경계에서만 한다. 피커는 날짜·시각을 모두 고른 뒤에만
 * 커밋하므로, 한쪽만 있는 부분 값은 빈 문자열이다.
 */
export function datetimeLocalToDateTimeValue(local: string): DateTimeValue {
  const match = DATETIME_LOCAL_PATTERN.exec(local.trim());
  if (match === null) {
    return { date: null, time: null };
  }
  const [, year, month, day, hour, minute] = match;
  return {
    date: new Date(Number(year), Number(month) - 1, Number(day)),
    time: { hours: Number(hour), minutes: Number(minute) },
  };
}

export function dateTimeValueToDatetimeLocal(value: DateTimeValue): string {
  if (value.date === null || value.time === null) {
    return "";
  }
  return `${value.date.getFullYear()}-${pad2(value.date.getMonth() + 1)}-${pad2(
    value.date.getDate(),
  )}T${pad2(value.time.hours)}:${pad2(value.time.minutes)}`;
}

// 기간 축의 오류. 어느 칸이 왜 틀렸는지 반환해서 화면이 그 자리에 표시한다. null 이면 문제가 없다.
export type ScheduleWindowIssue =
  | { field: "startAt"; message: string }
  | { field: "endAt"; message: string };

export const SCHEDULE_END_IN_PAST_MESSAGE = "종료 시각은 지금보다 뒤여야 합니다.";
export const SCHEDULE_END_BEFORE_START_MESSAGE = "종료 시각은 시작 시각보다 뒤여야 합니다.";
export const SCHEDULE_DATETIME_INVALID_MESSAGE = "날짜와 시각을 다시 확인해 주세요.";

/**
 * 기간 축이 유효한지 판정한다. 비운 축은 제약이 없다. 다음 경우는 잘못 채운 것이다.
 *   - 값을 채웠지만 파싱할 수 없다(존재하지 않는 시각 포함).
 *   - 끝이 시작보다 이르다(같으면 실행 창이 0).
 *   - 종료 시각이 이미 지났다. 상류는 받지만 ticker 가 영원히 건너뛴다. 그러면 활성 칩과 다음
 *     실행 시각은 보이지만 실행되지 않는 Schedule 이 된다.
 */
export function scheduleWindowIssue(
  draft: ScheduleDraft,
  nowMs: number = Date.now(),
): ScheduleWindowIssue | null {
  const startIso = datetimeLocalToUtcIso(draft.startAt);
  const endIso = datetimeLocalToUtcIso(draft.endAt);
  if (draft.startAt.trim() !== "" && startIso === null) {
    return { field: "startAt", message: SCHEDULE_DATETIME_INVALID_MESSAGE };
  }
  if (draft.endAt.trim() !== "" && endIso === null) {
    return { field: "endAt", message: SCHEDULE_DATETIME_INVALID_MESSAGE };
  }
  const endMs = endIso === null ? null : parseUtcMs(endIso);
  if (endMs !== null && endMs <= nowMs) {
    return { field: "endAt", message: SCHEDULE_END_IN_PAST_MESSAGE };
  }
  if (startIso !== null && endMs !== null) {
    const startMs = parseUtcMs(startIso);
    if (startMs !== null && endMs <= startMs) {
      return { field: "endAt", message: SCHEDULE_END_BEFORE_START_MESSAGE };
    }
  }
  return null;
}

export function isScheduleWindowValid(draft: ScheduleDraft, nowMs: number = Date.now()): boolean {
  return scheduleWindowIssue(draft, nowMs) === null;
}

/**
 * 스케줄을 등록할 수 있는 상태인지 판정한다. Schedule 스텝의 이탈 게이트다.
 *
 * cron 은 `validateCronExpression` 이 문법·최소 간격을 먼저 거른다. 최종 판정은 상류가 한다.
 */
export function isScheduleDraftReady(
  draft: ScheduleDraft,
  totalGpus: number | null = null,
): boolean {
  if (draft.name.trim() === "") {
    return false;
  }
  const recurrenceReady =
    draft.mode === "interval"
      ? scheduleIntervalSeconds(draft) !== null
      : draft.cron.trim() !== "" && validateCronExpression(draft.cron).kind === "valid";
  return (
    recurrenceReady &&
    scheduleMinFreeGpus(draft, totalGpus).kind !== "invalid" &&
    isScheduleWindowValid(draft)
  );
}

// cron 시간대의 기본값. 상류 기본은 UTC 지만 팀이 서울 시간으로 일정을 읽으므로 비운 값은 서울로
// 채워 보낸다. 생성·편집이 같은 값을 써야 두 화면의 판정이 같다.
export const DEFAULT_SCHEDULE_TIMEZONE = "Asia/Seoul";

export function resolveScheduleTimezone(timezone: string): string {
  const trimmed = timezone.trim();
  return trimmed === "" ? DEFAULT_SCHEDULE_TIMEZONE : trimmed;
}

// 초안 → 요청의 반복 축. 두 축은 배타라 고른 쪽만 보낸다(둘 다 보내면 BE 가 422).
export function scheduleRecurrence(draft: ScheduleDraft): TrainingScheduleRecurrence {
  if (draft.mode === "interval") {
    return { intervalSeconds: scheduleIntervalSeconds(draft) ?? undefined };
  }
  return {
    cron: draft.cron.trim(),
    timezone: resolveScheduleTimezone(draft.timezone),
  };
}

/**
 * 초안 → 생성 요청의 스케줄 필드 전체(제출 payload 제외).
 *
 * 생략 규칙은 이 한 곳이 소유한다(§16). 빈 설명·빈 기간은 키를 보내지 않는다. 실행 게이트는
 * 기본값(false·미설정)이면 보내지 않는다. 상류 기본값과 같은 값을 보내면 wire 에서 "설정한
 * 값"과 "바꾸지 않은 값"을 구분할 수 없다.
 */
export function scheduleCreateFields(
  draft: ScheduleDraft,
  // 여유 GPU 조건의 실질 상한. 판독기가 같은 상한을 읽어야 스텝이 막은 값이 wire 로 전달되지 않는다.
  totalGpus: number | null = null,
): Omit<CreateTrainingScheduleReq, "submit"> {
  const description = draft.description.trim();
  const startAt = datetimeLocalToUtcIso(draft.startAt);
  const endAt = datetimeLocalToUtcIso(draft.endAt);
  const minFreeGpus = scheduleMinFreeGpus(draft, totalGpus);
  return {
    name: draft.name.trim(),
    ...(description === "" ? {} : { description }),
    ...scheduleRecurrence(draft),
    ...(startAt === null ? {} : { startAt }),
    ...(endAt === null ? {} : { endAt }),
    ...(draft.skipIfUnchanged ? { skipIfUnchanged: true } : {}),
    ...(minFreeGpus.kind === "value" ? { minFreeGpus: minFreeGpus.value } : {}),
    ...(draft.trackLatestEvalRelease ? { trackLatestEvalRelease: true } : {}),
  };
}
